import React from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  cn,
  EmptyState,
  Flex,
  Heading,
  LoadingOrbit,
  Text,
} from '@chatsdet/ui';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { toUrlId } from '../../../lib/prefixed-ids';
import { useOrgEvents, type OrgEvent } from '../../../hooks/useOrgEvents';
import {
  renderActivity,
  TIMELINE_EVENT_TYPES,
  type ActivityAccent,
  type ActivityLink,
} from './activity-renderers';

/**
 * Tinted, bordered icon chips matching the PlaybookViewer's node-type badge
 * style (`bg-{color}-500/10 text-{color}-600 dark:text-{color}-400
 * border-{color}-500/20`) for visual consistency across the app.
 */
const accentChipStyles: Record<ActivityAccent, string> = {
  success: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  info: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  warning: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
  muted: 'bg-muted text-muted-foreground border-border',
  brand: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
};

function ActivityRow({ event, onSelect }: { event: OrgEvent; onSelect: (link: ActivityLink) => void }) {
  const descriptor = renderActivity(event.eventType, {
    metadata: event.metadata,
    actorName: event.actorName,
  });
  const actor = event.actorName ?? 'Cofactor';
  const createdAt = new Date(event.createdAt);
  const relative = formatDistanceToNow(createdAt, { addSuffix: true });
  const clickable = !!descriptor.link;

  // Compact vertical row for the narrow (1/3-width) activity column: tinted icon
  // chip on the left, then a wrapping title with the timestamp + badge stacked
  // beneath it (rather than pushed to a far-right edge that the column lacks).
  const content = (
    <>
      <span
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border',
          accentChipStyles[descriptor.accent]
        )}
      >
        {descriptor.icon}
      </span>
      <span className="min-w-0 flex-1">
        <Text size="sm" as="span" className="block">
          <span className="font-medium text-foreground">{actor}</span> {descriptor.title}
        </Text>
        <span className="mt-0.5 flex items-center gap-2">
          <Text size="xs" className="text-muted-foreground" title={createdAt.toLocaleString()}>
            {relative}
          </Text>
          {descriptor.badge && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
              {descriptor.badge}
            </span>
          )}
        </span>
      </span>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={() => descriptor.link && onSelect(descriptor.link)}
        className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/60"
      >
        {content}
      </button>
    );
  }

  return <div className="flex items-start gap-2.5 px-0 py-2">{content}</div>;
}

export function RecentActivity({ className }: { className?: string }) {
  const navigate = useNavigate();
  const query = useOrgEvents(TIMELINE_EVENT_TYPES, 20);

  const onSelect = React.useCallback(
    (link: ActivityLink) => {
      switch (link.kind) {
        case 'flow':
          navigate({ to: '/flows/$workflowId', params: { workflowId: toUrlId('wf', link.workflowId) } });
          break;
        case 'runs':
          navigate({ to: '/runs' });
          break;
        case 'execution':
          navigate({
            to: '/runs/test/$executionId',
            params: { executionId: link.executionId },
          });
          break;
      }
    },
    [navigate]
  );

  let body: React.ReactNode;
  if (query.isLoading) {
    body = (
      <Flex align="center" justify="center" className="py-8">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  } else if (query.isError) {
    body = (
      <Flex direction="column" gap="md" align="start" className="py-2">
        <Text size="sm" className="text-muted-foreground">
          Unable to load recent activity right now.
        </Text>
        <Button variant="outline" onPress={() => query.refetch()}>
          Retry
        </Button>
      </Flex>
    );
  } else if (!query.data || query.data.length === 0) {
    body = (
      <EmptyState
        icon={<ClockCounterClockwise size={28} />}
        title="No recent activity yet"
        description="Flow approvals, suite runs, and Cofactor maintenance will show up here."
        size="sm"
      />
    );
  } else {
    body = (
      <div className="flex flex-col">
        {query.data.map((event) => (
          <ActivityRow key={event.id} event={event} onSelect={onSelect} />
        ))}
      </div>
    );
  }

  return (
    <Card padding="lg" className={className}>
      <CardHeader>
        <Flex align="center" gap="sm">
          <ClockCounterClockwise size={18} />
          <Heading level={4}>Recent activity</Heading>
        </Flex>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
